import { Box, HStack, Stack, Text, Textarea } from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import * as React from "react";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { z } from "zod";

import {
  getBrandMessage,
  updateBrandMessage,
} from "../../../api/company-knowledge";
import { Button } from "../../../components/button";
import PageBody from "../../../components/page-body";
import { toaster } from "../../../components/toaster";
import { useLoading } from "../../../hooks";
import { BrandMessage } from "../../../types/company-knowledge";

// Labels come from the original spec comments in
// audiences/components/company-profile.rkt (edited out alongside the
// migration); wording should be re-checked at review. All columns are
// nullable, so the schema carries no validators -- it exists for type
// inference and consistency with the form pattern.

const brandMessageSchema = z.object({
  "expected-tone": z.string(),
  "preferred-terms": z.string(),
  examples: z.string(),
  "terms-to-avoid": z.string(),
  nuances: z.string(),
});

type BrandMessageFormData = z.infer<typeof brandMessageSchema>;

export default function BrandMessagePage() {
  const { workspace: workspaceSlug } = useParams();
  const [message, setMessage] = React.useState<BrandMessage | null>(null);
  const [, withLoading] = useLoading<void>(true);
  const [isSaving, withSaving] = useLoading<void>(false);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<BrandMessageFormData>({
    defaultValues: emptyMessage,
    resolver: zodResolver(brandMessageSchema),
  });

  React.useEffect(() => {
    if (!workspaceSlug) return;
    void withLoading(async () => {
      setMessage(await getBrandMessage(workspaceSlug));
    });
  }, [withLoading, workspaceSlug]);

  React.useEffect(() => {
    if (!message) return;
    reset(toFormData(message));
  }, [message, reset]);

  const onSubmit = handleSubmit(async (data) => {
    if (!workspaceSlug) return;
    await withSaving(async () => {
      await updateBrandMessage(workspaceSlug, data);
      toaster.create({ type: "success", title: "Brand message updated" });
    });
  });

  return (
    <PageBody>
      <KnowledgeCard
        title="Brand & Communication"
        sub="How you sound when you talk to customers."
      >
        <form onSubmit={onSubmit}>
          <Stack gap="1">
            <KnowledgeField
              label="Tone of voice"
              error={errors["expected-tone"]?.message}
              input={
                <Textarea
                  {...register("expected-tone")}
                  placeholder="How you want your company to come across in its messaging."
                  rows={3}
                />
              }
            />
            <KnowledgeField
              label="Common phrasing and preferred terminology"
              error={errors["preferred-terms"]?.message}
              input={
                <Textarea
                  {...register("preferred-terms")}
                  placeholder="Words and phrases you prefer to describe your business with."
                  rows={3}
                />
              }
            />
            <KnowledgeField
              label="Messaging examples"
              error={errors.examples?.message}
              input={
                <Textarea
                  {...register("examples")}
                  placeholder="Existing copy that sounds like your brand."
                  rows={3}
                />
              }
            />
            <KnowledgeField
              label="Words and phrasing to avoid"
              error={errors["terms-to-avoid"]?.message}
              input={
                <Textarea
                  {...register("terms-to-avoid")}
                  placeholder="Language that does not sound like your brand or could misrepresent it. Put factual product limitations in Company & Product."
                  rows={3}
                />
              }
            />
            <KnowledgeField
              label="Other brand nuances"
              error={errors.nuances?.message}
              input={
                <Textarea
                  {...register("nuances")}
                  placeholder="Anything else worth knowing about how you communicate."
                  rows={3}
                />
              }
            />
            <HStack marginTop="4">
              <Button loading={isSaving} type="submit" variant="solid">
                Update
              </Button>
            </HStack>
          </Stack>
        </form>
      </KnowledgeCard>
    </PageBody>
  );
}

const emptyMessage: BrandMessageFormData = {
  "expected-tone": "",
  "preferred-terms": "",
  examples: "",
  "terms-to-avoid": "",
  nuances: "",
};

// The schema's fields are nullable strings; empty strings are the
// form's representation of "unset".
const toFormData = (message: BrandMessage): BrandMessageFormData => ({
  "expected-tone": message["expected-tone"] ?? "",
  "preferred-terms": message["preferred-terms"] ?? "",
  examples: message.examples ?? "",
  "terms-to-avoid": message["terms-to-avoid"] ?? "",
  nuances: message.nuances ?? "",
});

// SettingsCard equivalent, local to this page for now.
function KnowledgeCard({
  title,
  sub,
  children,
}: {
  title: string;
  sub?: string;
  children: React.ReactNode;
}) {
  return (
    <Box
      background="white"
      borderRadius="l3"
      boxShadow="card"
      overflow="hidden"
    >
      <Box
        borderBottomWidth="1"
        borderColor="border.soft"
        paddingX="6"
        paddingY="4"
      >
        <Text color="fg.ink" fontSize="md" fontWeight="medium">
          {title}
        </Text>
        {sub && (
          <Text color="fg.muted" fontSize="sm" marginTop="0.5">
            {sub}
          </Text>
        )}
      </Box>
      <Box paddingBottom="6" paddingTop="5" paddingX="6">
        {children}
      </Box>
    </Box>
  );
}

// FormRow equivalent, local to this page for now.
function KnowledgeField({
  label,
  error,
  input,
}: {
  label: string;
  error?: string;
  input: React.ReactNode;
}) {
  return (
    <Stack gap="1" paddingY="2">
      <Text color="fg.muted" fontSize="sm">
        {label}
      </Text>
      {input}
      {error && (
        <Text color="red.500" fontSize="xs">
          {error}
        </Text>
      )}
    </Stack>
  );
}
